import mixpanel from 'mixpanel-browser'
import posthog from 'posthog-js'

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN
const MIXPANEL_API_HOST = process.env.REACT_APP_MIXPANEL_API_HOST // e.g., https://api-eu.mixpanel.com

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com'

// Hardcoded Session Replay defaults (adjust as needed)
const MIXPANEL_RECORD_SESSIONS_PERCENT = process.env.NODE_ENV !== 'production' ? 100 : 10
const MIXPANEL_HEATMAPS = true
const MIXPANEL_IDLE_TIMEOUT_MS = 30 * 60 * 1000

const isBrowser = typeof window !== 'undefined'

let mixpanelReady = false
let posthogReady = false

let mixpanelInitAttempted = false
let posthogInitAttempted = false

function initMixpanel() {
  if (mixpanelReady || mixpanelInitAttempted) return
  mixpanelInitAttempted = true
  if (!MIXPANEL_TOKEN) {
    console.warn('Mixpanel token not set (REACT_APP_MIXPANEL_TOKEN). Mixpanel disabled.')
    return
  }
  try {
    const initOptions = {
      debug: process.env.NODE_ENV !== 'production',
      track_pageview: false,
      persistence: 'localStorage'
    }
    if (MIXPANEL_API_HOST) initOptions.api_host = MIXPANEL_API_HOST
    if (!Number.isNaN(MIXPANEL_RECORD_SESSIONS_PERCENT) && MIXPANEL_RECORD_SESSIONS_PERCENT > 0) {
      initOptions.record_sessions_percent = MIXPANEL_RECORD_SESSIONS_PERCENT
    }
    initOptions.record_heatmap_data = MIXPANEL_HEATMAPS
    initOptions.record_idle_timeout_ms = MIXPANEL_IDLE_TIMEOUT_MS
    mixpanel.init(MIXPANEL_TOKEN, initOptions)
    mixpanelReady = true
    if (process.env.NODE_ENV !== 'production' && typeof mixpanel.start_session_recording === 'function') {
      try { mixpanel.start_session_recording() } catch (_) {}
    }
  } catch (e) {
    console.warn('Failed to init Mixpanel:', e)
  }
}

function initPostHog() {
  if (posthogReady || posthogInitAttempted) return
  posthogInitAttempted = true
  if (!POSTHOG_KEY) {
    console.warn('PostHog key not set (REACT_APP_POSTHOG_KEY). PostHog disabled.')
    return
  }
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Opt into PostHog's January 30, 2026 defaults — enables latest sensible
      // defaults for autocapture, session recording, and exception tracking.
      // See https://posthog.com/docs/libraries/js/config
      defaults: '2026-01-30',
      person_profiles: 'identified_only',
      // We fire 'Page View' manually in App.js to keep route tracking accurate
      // for SPA navigations, so disable PostHog's auto pageview here.
      capture_pageview: false,
      loaded: () => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[PostHog] initialized', POSTHOG_HOST)
        }
      },
    })
    posthogReady = true
  } catch (e) {
    console.warn('Failed to init PostHog:', e)
  }
}

export function initAnalytics() {
  if (!isBrowser) return
  initMixpanel()
  initPostHog()
}

export function identifyUser(user) {
  if (!isBrowser || !user) return
  try {
    const userId = user.id || user.uid || user.user_id
    const email = user.email || user.user_metadata?.email || user.email_address
    const name = user.user_metadata?.full_name || user.user_metadata?.name || user.name
    const createdAt = user.created_at || user.createdAt

    const peopleProps = {}
    if (name) peopleProps['$name'] = name
    if (email) peopleProps['$email'] = email
    if (createdAt) peopleProps['created_at'] = createdAt

    if (mixpanelReady && userId) {
      mixpanel.identify(userId)
      if (Object.keys(peopleProps).length > 0) mixpanel.people.set(peopleProps)
    }

    if (posthogReady && userId) {
      const phProps = {}
      if (email) phProps.email = email
      if (name) phProps.name = name
      if (createdAt) phProps.created_at = createdAt
      posthog.identify(userId, phProps)
    }
  } catch (e) {
    console.warn('Failed to identify user:', e)
  }
}

export function trackEvent(eventName, props = {}) {
  if (!isBrowser || !eventName) return
  try {
    if (mixpanelReady) mixpanel.track(eventName, props)
    if (posthogReady) posthog.capture(eventName, props)
  } catch (e) {
    console.warn('Failed to track event:', e)
  }
}

export function resetAnalytics() {
  if (!isBrowser) return
  try {
    if (mixpanelReady) mixpanel.reset()
    if (posthogReady) posthog.reset()
  } catch (e) {
    console.warn('Failed to reset analytics:', e)
  }
}

export function startSessionReplay() {
  if (!isBrowser) return
  try {
    if (mixpanelReady && mixpanel?.start_session_recording) mixpanel.start_session_recording()
    if (posthogReady && posthog?.startSessionRecording) posthog.startSessionRecording()
  } catch (e) {
    console.warn('Failed to start session replay:', e)
  }
}

export function stopSessionReplay() {
  if (!isBrowser) return
  try {
    if (mixpanelReady && mixpanel?.stop_session_recording) mixpanel.stop_session_recording()
    if (posthogReady && posthog?.stopSessionRecording) posthog.stopSessionRecording()
  } catch (e) {
    console.warn('Failed to stop session replay:', e)
  }
}

export function getCurrentReplayInfo() {
  try {
    if (!isBrowser) return {}
    if (!mixpanelReady && !posthogReady) initAnalytics()
    const out = {}
    if (mixpanelReady) {
      const props = typeof mixpanel.get_session_recording_properties === 'function'
        ? (mixpanel.get_session_recording_properties() || {})
        : {}
      const url = typeof mixpanel.get_session_replay_url === 'function'
        ? mixpanel.get_session_replay_url()
        : null
      Object.assign(out, props, url ? { mixpanel_replay_url: url } : {})
    }
    if (posthogReady && typeof posthog.get_session_replay_url === 'function') {
      try { out.posthog_replay_url = posthog.get_session_replay_url() } catch (_) {}
    }
    return out
  } catch (e) {
    return {}
  }
}
